/**
 * Announcement bar rotator.
 *
 * Keeps the bar to a single line by showing one message at a time and
 * crossfading between them, instead of stacking every message as the static
 * markup would on a narrow screen.
 *
 * Rotation pauses on hover and on keyboard focus, and does not start at all
 * when the visitor prefers reduced motion — in that case the first message
 * simply stays put, so nothing moves on its own.
 */
class SquishAnnouncement extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.sq-ann__item')];
    if (this.items.length < 2) return;

    this.index = 0;
    this.timer = null;
    this.speed = (Number(this.dataset.speed) || 4) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.reduced.addEventListener('change', () => this.restart());

    // Pause while the tab is hidden; a background timer only wastes work.
    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  show(next) {
    const count = this.items.length;
    this.index = ((next % count) + count) % count;
    this.items.forEach((item, i) => {
      const on = i === this.index;
      item.classList.toggle('is-active', on);
      item.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  start() {
    this.stop();
    if (this.reduced.matches) return;
    this.timer = setInterval(() => this.show(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }
}

customElements.define('squish-announcement', SquishAnnouncement);
